import { ChangeDetectorRef, Component, Input, OnChanges, SimpleChanges, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';

import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDividerModule } from '@angular/material/divider';

import { BaaService } from '../../service/entity-services/baa.service';
import { DocumentViewerService, DocumentViewerResult } from '../../service/document-viewer.service';
import { Baa } from '../../objects/baa';

@Component({
  selector: 'app-baa-detail-inline',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatIconModule,
    MatButtonModule,
    MatChipsModule,
    MatProgressSpinnerModule,
    MatDividerModule
  ],
  templateUrl: './baa-detail-inline.component.html',
  styleUrls: ['./baa-detail-inline.component.css']
})
export class BaaDetailInlineComponent implements OnChanges {
  @Input() baaNum: string = '';

  private baaService = inject(BaaService);
  private documentViewerService = inject(DocumentViewerService);
  /** Zoneless: a post-`await` assignment must notify Angular explicitly. */
  private cdrZoneless = inject(ChangeDetectorRef);
  private router = inject(Router);

  loading = false;
  baa: Baa | null = null;
  document: DocumentViewerResult | null = null;
  showDocument = false;

  // Display-friendly values
  businessAssociateName = '';
  coveredEntityName = '';
  primaryOwnerName = '';

  ngOnChanges(changes: SimpleChanges) {
    if (changes['baaNum'] && this.baaNum) {
      this.loadBaa();
    }
  }

  private loadBaa() {
    this.loading = true;
    this.baa = null;
    this.document = null;

    this.baaService.getBaaByNumber(this.baaNum).subscribe(async baa => {
      if (baa) {
        this.baa = baa;
        this.processDisplayNames();
        if (baa.linkToBaa) {
          this.document = await this.documentViewerService.processDocument(baa.linkToBaa);
          this.cdrZoneless.markForCheck();   // zoneless: notify after await
        }
      }
      this.loading = false;
    });
  }

  private processDisplayNames() {
    if (!this.baa) return;
    this.businessAssociateName = this.extractName(this.baa.businessAssociateName, 'businessAssociateNameFriendlyName');
    this.coveredEntityName = this.extractName(this.baa.coveredEntity, 'coveredEntityFriendlyName');
    this.primaryOwnerName = this.extractOwnerName(this.baa.primaryOwner);
  }

  private extractName(value: any, friendlyKey?: string): string {
    if (!value) return 'N/A';
    if (typeof value === 'string') return value;
    if (friendlyKey && this.baa && (this.baa as any)[friendlyKey]) {
      return (this.baa as any)[friendlyKey];
    }
    if (typeof value === 'object') {
      return value.name || value.vendorName || value.legalEntityName || 'N/A';
    }
    return 'N/A';
  }

  private extractOwnerName(owner: any): string {
    if (!owner) return 'Not Assigned';
    if (typeof owner === 'string') return owner;
    if (owner.userData) {
      const ud = owner.userData;
      if (ud.family_name && ud.given_name) return `${ud.family_name}, ${ud.given_name}`;
      return ud.email || 'Not Assigned';
    }
    if (owner.fullName) return owner.fullName;
    if (owner.firstName || owner.lastName) {
      return [owner.lastName, owner.firstName].filter(Boolean).join(', ');
    }
    return owner.email || 'Not Assigned';
  }

  formatDate(date: string | undefined): string {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString();
  }

  toggleDocument() {
    this.showDocument = !this.showDocument;
  }

  downloadDocument() {
    if (this.document) {
      this.documentViewerService.downloadFile(this.document.fetchUrl, this.document.fileName);
    }
  }

  viewFullRecord() {
    if (this.baa?.baaId) {
      this.router.navigate(['/securehome/baa', this.baa.baaId]);
    }
  }
}
